import React from 'react';
import PropTypes from 'prop-types';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import Label from 'core/components/label/label';
import Button from 'core/components/button/button';
import DownloadButton from 'tools/helpers/generate-task-ui/components/download-button';
import useStyles from './preview-progress-dialog.styles';

/**
 * Live progress for the preview AND apply flows (`verb` picks the wording), fed by the
 * `onTaskLiveOutput` subscription. On demo a preview run is EXPECTED to fail with "BigQuery
 * unavailable" — that beats silently reporting zero counts.
 */
const PreviewProgressDialog = ({ open, onClose, messages, filePaths, error, isRunning, verb, taskRunId }) => {
  const classes = useStyles();

  let title = `${verb} complete`;
  if (isRunning) {
    title = `Running ${verb.toLowerCase()}…`;
  } else if (error) {
    title = `${verb} failed`;
  }

  return (
    // Always closable, even while running: the terminal message is delivered over a subscription that
    // can be established after the lambda has already published, so `isRunning` can stick on true for
    // a run that already failed. Gating close on it would trap the operator in the modal.
    <BaseDialog className={classes.modal} onClose={onClose} open={open}>
      <div className={classes.container}>
        <Label className={classes.mainLabel} type="green75Big">
          {title}
        </Label>

        {messages.map((message, index) => (
          // Append-only, so the index is stable.
          // eslint-disable-next-line react/no-array-index-key
          <Label key={index} type="infoValue">
            {message}
          </Label>
        ))}

        {error && <Label type="infoLabel">{error}</Label>}

        {/* taskRunId, not the presigned path: these links are signed with the lambda's STS
            credentials and die within hours, so DownloadButton re-mints one per click. */}
        {filePaths.map((file) => (
          <div key={file.output}>
            <Label type="infoLabel">{file.output}</Label>
            <DownloadButton taskRunId={taskRunId} output={file.output} path={file.path} />
          </div>
        ))}

        <div className={classes.buttonContainer}>
          <Button className={classes.buttons} variant="contained" color="primary" onClick={onClose}>
            {isRunning ? 'Close (keeps running)' : 'Close'}
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

PreviewProgressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  messages: PropTypes.arrayOf(PropTypes.string).isRequired,
  filePaths: PropTypes.arrayOf(PropTypes.shape({ output: PropTypes.string, path: PropTypes.string })),
  error: PropTypes.string,
  isRunning: PropTypes.bool,
  verb: PropTypes.string,
  taskRunId: PropTypes.string
};

PreviewProgressDialog.defaultProps = {
  filePaths: [],
  error: null,
  isRunning: false,
  verb: 'Preview',
  taskRunId: null
};

export default PreviewProgressDialog;
